import { motion } from 'framer-motion'
import SectionTitle from './SectionTitle'

const Products = () => {
  const products = [
    {
      name: 'Efandex',
      description: 'Efandex is a platform that allows homeowners to list unused spaces such as patios, balconies, and outdoor areas for private events. By connecting property owners with individuals seeking unique venues, Efandex helps maximize the value of underutilized spaces while offering flexible event options.',
      logo: 'efandex',
      hasExplore: true,
      status: null,
    },
    {
      name: 'DS Pickup (In Development)',
      description: 'DS Pickup is a service-oriented application that simplifies vehicle maintenance logistics. Car owners can request registered drivers to pick up their vehicles and deliver them to service centers for repairs or routine maintenance. The app is designed to save time, reduce hassle, and ensure secure handling of vehicles.',
      logo: 'dspickup',
      hasExplore: false,
      status: 'IN DEVELOPMENT',
    },
    {
      name: 'Finscor',
      description: 'Finscor is a sports prediction application designed to engage fans in a new way. Users can predict game outcomes, track their performance, and earn rewards for accurate predictions. Built with compliance and fairness in mind, Finscor aims to combine entertainment with friendly competition.',
      logo: 'finscor',
      hasExplore: false,
      status: 'IN DEVELOPMENT',
    },
  ]

  // Logo components for each product
  const EfandexLogo = () => (
    <img src="/efandex_img.png" className="w-20 h-20 sm:w-24 sm:h-24 lg:w-48 lg:h-48" alt='Efandex logo' />
  )

  const DSPickupLogo = () => (
    <img src='/DS_Pickup_img.png' className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32" alt='DS Pickup logo' />
  )

  const FinscorLogo = () => (
    <img src='/finscor_img.png' className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32" alt='Finscor logo' />
  )

  const renderLogo = (logoType) => {
    switch (logoType) {
      case 'efandex':
        return <EfandexLogo />
      case 'dspickup':
        return <DSPickupLogo />
      case 'finscor':
        return <FinscorLogo />
      default:
        return null
    }
  }

  return (
    <section id="products" className="bg-ds-off-white py-16 sm:py-20 lg:py-28">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-12">
        <SectionTitle title="PRODUCTS" />

        <div className="grid lg:grid-cols-[35%_1fr] gap-4 sm:gap-6 lg:gap-8">
          {/* Efandex - Large card on left */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{ x: 20 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6 }}
            className="product-card p-6 sm:p-8 lg:p-12 flex flex-col rounded-tr-3xl rounded-br-3xl items-center justify-center text-center -ml-6 sm:-ml-8 lg:-ml-16 min-h-[600px]"
          >
            {renderLogo(products[0].logo)}
            <h3 className="font-heading text-xl sm:text-2xl lg:text-3xl font-semibold text-ds-navy mt-4 sm:mt-6 mb-3 sm:mb-4">
              {products[0].name}
            </h3>
            <p className="font-body text-sm sm:text-lg text-ds-navy leading-relaxed mb-4 sm:mb-6">
              {products[0].description}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <motion.a
                href="https://apps.apple.com/us/app/efandex/id6759389602"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-outline rounded-xl bg-transparent text-ds-navy border-ds-navy hover:text-ds-blue hover:border-ds-blue px-5 py-2.5 flex items-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                App Store
              </motion.a>
              <motion.a
                href="https://play.google.com/store/apps/details?id=app.splenify.efandex"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-outline rounded-xl bg-transparent text-ds-navy border-ds-navy hover:text-ds-blue hover:border-ds-blue px-5 py-2.5 flex items-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27zm3.35-4.31c.34.27.54.68.54 1.19 0 .51-.2.92-.54 1.19l-2.12 1.23-2.5-2.5 2.5-2.5 2.12 1.39zM6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"/>
                </svg>
                Play Store
              </motion.a>
            </div>
            <div className="fmt mt-3 sm:mt-5">
              <motion.a
                href="https://www.efandex.com/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-outline rounded-xl bg-transparent text-ds-navy border-ds-navy hover:text-ds-blue hover:border-ds-blue px-5 py-2.5 flex items-center gap-2"
              >
                EXPLORE
              </motion.a>
            </div>
          </motion.div>

          {/* Right column - DS Pickup and Finscor */}
          <div className="space-y-8 sm:space-y-16 lg:space-y-24">
            {products.slice(1).map((product, index) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ x: -20 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                className="product-card move-to-left p-5 sm:p-6 lg:p-12 flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 rounded-bl-3xl rounded-tl-3xl"
              >
                {renderLogo(product.logo)}
                <div className="text-center sm:text-left flex-1">
                  <h3 className="font-heading text-base sm:text-lg lg:text-xl font-semibold text-ds-navy mb-2 sm:mb-3">
                    {product.name}
                  </h3>
                  <p className="font-body text-sm lg:text-lg text-ds-navy leading-relaxed mb-3 sm:mb-4">
                    {product.description}
                  </p>
                  {product.status && (
                    <span className="dev-badge">{product.status}</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Products
